import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Form, FormGroup, Label, Input, Button, Card, CardBody, CardHeader, Alert, InputGroup, InputGroupAddon } from 'reactstrap';
import { checkResetToken, resetPassword } from '../../../api/resetPassword';
import './styles.css';

const SetPassword = () => {
    const [formData, setFormData] = useState({
        newPassword: '',
        confirmPassword: ''
    });
    
    const [showPasswords, setShowPasswords] = useState({
        newPassword: false,
        confirmPassword: false
    });
    
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isValidating, setIsValidating] = useState(true);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [token, setToken] = useState('');
    const [isTokenValid, setIsTokenValid] = useState(false);
    
    const history = useHistory();
    const location = useLocation();

    // Extraire le token depuis l'URL
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const tokenFromUrl = urlParams.get('token');
        
        if (!tokenFromUrl) {
            setErrorMessage('Token manquant dans l\'URL');
            setIsValidating(false);
            return;
        }
        
        setToken(tokenFromUrl);
        validateToken(tokenFromUrl);
    }, [location]);

    // Valider le token
    const validateToken = async (tokenToValidate) => {
        try {
            setIsValidating(true);
            const response = await checkResetToken(tokenToValidate);
            
            if (response.status === 200) {
                setIsTokenValid(true);
                setSuccessMessage('Token valide. Vous pouvez maintenant définir votre nouveau mot de passe.');
            }
        } catch (error) {
            console.error('Erreur validation token:', error);
            if (error.response?.status === 400 || error.response?.status === 401) {
                setErrorMessage('Token invalide ou expiré. Veuillez demander un nouveau lien de réinitialisation.');
            } else {
                setErrorMessage('Erreur lors de la validation du token. Veuillez réessayer.');
            }
            setIsTokenValid(false);
        } finally {
            setIsValidating(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.newPassword.trim()) {
            newErrors.newPassword = 'Le nouveau mot de passe est requis';
        } else if (formData.newPassword.length < 8) {
            newErrors.newPassword = 'Le nouveau mot de passe doit contenir au moins 8 caractères';
        } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.newPassword)) {
            newErrors.newPassword = 'Le mot de passe doit contenir au moins une minuscule, une majuscule et un chiffre';
        }

        if (!formData.confirmPassword.trim()) {
            newErrors.confirmPassword = 'La confirmation du mot de passe est requise';
        } else if (formData.newPassword !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        setSuccessMessage('');
        setErrorMessage('');

        try {
            const response = await resetPassword(
                token,
                formData.newPassword,
                formData.confirmPassword
            );
            
            if (response.status === 200) {
                setSuccessMessage('Mot de passe réinitialisé avec succès ! Vous allez être redirigé vers la page de connexion.');
                
                // Rediriger vers la page de login après 3 secondes
                setTimeout(() => {
                    history.push('/login');
                }, 3000);
            }
            
        } catch (error) {
            console.error('Erreur réinitialisation:', error);
            if (error.response?.data?.message) {
                setErrorMessage(error.response.data.message);
            } else if (error.response?.data?.errorMessage) {
                setErrorMessage(error.response.data.errorMessage);
            } else {
                setErrorMessage('Une erreur est survenue lors de la réinitialisation du mot de passe');
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (isValidating) {
        return (
            <div className="set-password-container">
                <div className="row justify-content-center">
                    <div className="col-md-8 col-lg-6 col-xl-5">
                        <Card className="mt-4 set-password-card">
                            <CardBody className="text-center p-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="sr-only">Chargement...</span>
                                </div>
                                <p className="mt-3">Validation du token en cours...</p>
                            </CardBody>
                        </Card>
                    </div>
                </div>
            </div>
        );
    }

    if (!isTokenValid) {
        return (
            <div className="set-password-container">
                <div className="row justify-content-center">
                    <div className="col-md-8 col-lg-6 col-xl-5">
                        <Card className="mt-4 set-password-card">
                            <CardHeader className="text-center">
                                <h4 className="mb-0">Token invalide</h4>
                            </CardHeader>
                            <CardBody className="p-4">
                                <Alert color="danger">
                                    {errorMessage}
                                </Alert>
                                <div className="text-center">
                                    <Button color="primary" onClick={() => history.push('/forgot-password')}>
                                        Demander un nouveau lien
                                    </Button>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="set-password-container">
            <div className="row justify-content-center">
                <div className="col-md-8 col-lg-6 col-xl-5">
                    <Card className="mt-4 set-password-card">
                        <CardHeader className="text-center">
                            <h4 className="mb-0">Définir un nouveau mot de passe</h4>
                        </CardHeader>
                        <CardBody className="p-4">
                            {successMessage && (
                                <Alert color="success" className="mb-3">
                                    {successMessage}
                                </Alert>
                            )}
                            
                            {errorMessage && (
                                <Alert color="danger" className="mb-3">
                                    {errorMessage}
                                </Alert>
                            )}

                            <Form onSubmit={handleSubmit}>
                                <FormGroup>
                                    <Label for="newPassword">
                                        Nouveau mot de passe <span className="text-danger">*</span>
                                    </Label>
                                    <InputGroup className="password-input-group">
                                        <Input
                                            type={showPasswords.newPassword ? 'text' : 'password'}
                                            name="newPassword"
                                            id="newPassword"
                                            value={formData.newPassword}
                                            onChange={handleInputChange}
                                            invalid={!!errors.newPassword}
                                            placeholder="Entrez votre nouveau mot de passe"
                                            className="password-input"
                                        />
                                        <InputGroupAddon addonType="append">
                                            <Button
                                                type="button"
                                                color="outline-secondary"
                                                onClick={() => togglePasswordVisibility('newPassword')}
                                                size="sm"
                                                className="password-toggle-btn"
                                            >
                                                {showPasswords.newPassword ? 'Masquer' : 'Voir'}
                                            </Button>
                                        </InputGroupAddon>
                                    </InputGroup>
                                    {errors.newPassword && (
                                        <div className="text-danger small mt-1">{errors.newPassword}</div>
                                    )}
                                    <small className="form-text text-muted">
                                        Le mot de passe doit contenir au moins 8 caractères, une minuscule, une majuscule et un chiffre.
                                    </small>
                                </FormGroup>

                                <FormGroup>
                                    <Label for="confirmPassword">
                                        Confirmer le nouveau mot de passe <span className="text-danger">*</span>
                                    </Label>
                                    <InputGroup className="password-input-group">
                                        <Input
                                            type={showPasswords.confirmPassword ? 'text' : 'password'}
                                            name="confirmPassword"
                                            id="confirmPassword"
                                            value={formData.confirmPassword}
                                            onChange={handleInputChange}
                                            invalid={!!errors.confirmPassword}
                                            placeholder="Confirmez votre nouveau mot de passe"
                                            className="password-input"
                                        />
                                        <InputGroupAddon addonType="append">
                                            <Button
                                                type="button"
                                                color="outline-secondary"
                                                onClick={() => togglePasswordVisibility('confirmPassword')}
                                                size="sm"
                                                className="password-toggle-btn"
                                            >
                                                {showPasswords.confirmPassword ? 'Masquer' : 'Voir'}
                                            </Button>
                                        </InputGroupAddon>
                                    </InputGroup>
                                    {errors.confirmPassword && (
                                        <div className="text-danger small mt-1">{errors.confirmPassword}</div>
                                    )}
                                </FormGroup>

                                <div className="d-flex gap-2 mt-4">
                                    <Button
                                        type="submit"
                                        color="primary"
                                        className="flex-fill"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
                                    </Button>
                                </div>
                            </Form>
                        </CardBody>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default SetPassword;
